/**
 * Hub monitoring pages.
 *   Audit             GET v3/hub/admin/audit (newest first; "Older" pages with beforeId)
 *   Stuck deliveries  GET v3/hub/admin/asks/deliveries?status=failed|email|legacy|all
 * Read-only. Each call is itself recorded in the Hub's admin audit.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Banner, Button, Pill, Spinner } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { SelectField, TextField } from '@/components/FormField';
import {
  ConsentWaitingError,
  HubApiError,
  hubApi,
  type DeliveryStatusFilter,
  type HubAuditEntry,
  type HubDelivery,
  type HubDeliveryCount,
} from './hubApi';

const PAGE = 100;

function errorText(e: unknown): string {
  if (e instanceof ConsentWaitingError) return e.message;
  if (e instanceof HubApiError) return e.code ? `${e.message} (${e.code})` : e.message;
  return e instanceof Error ? e.message : String(e);
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function HubAuditPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [route, setRoute] = useState(params.get('route') ?? '');
  const [actor, setActor] = useState(params.get('actor') ?? '');
  const [since, setSince] = useState(params.get('since') ?? '');
  const [entries, setEntries] = useState<HubAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  const load = useCallback(
    async (beforeId?: number) => {
      setLoading(true);
      setError(null);
      try {
        const r = await hubApi.audit({
          top: PAGE,
          route: params.get('route') ?? undefined,
          actor: params.get('actor') ?? undefined,
          since: params.get('since') ? new Date(params.get('since')!).toISOString() : undefined,
          beforeId,
        });
        setEntries((prev) => (beforeId ? [...prev, ...r.entries] : r.entries));
        setMore(r.entries.length === PAGE);
      } catch (e) {
        setError(errorText(e));
      } finally {
        setLoading(false);
      }
    },
    [params],
  );

  useEffect(() => {
    setEntries([]);
    void load();
  }, [load]);

  const apply = (): void => {
    const next = new URLSearchParams();
    if (route.trim()) next.set('route', route.trim());
    if (actor.trim()) next.set('actor', actor.trim());
    if (since.trim()) next.set('since', since.trim());
    setParams(next);
  };

  const last = entries[entries.length - 1];

  return (
    <div>
      <h2>Hub admin audit</h2>
      <p className="page-intro">
        Every call to the Hub&rsquo;s admin routes, reads included, with the account that made it. Newest first.
      </p>
      <div className="toolbar">
        <TextField label="Route (end with * for a prefix)" value={route} onChange={setRoute} placeholder="v3/hub/admin/settings*" />
        <TextField label="Actor" value={actor} onChange={setActor} placeholder="name@mypcb.com" />
        <label className="form-field">
          <span className="form-field__label">Since</span>
          <input type="date" className="form-input" value={since} onChange={(e) => setSince(e.target.value)} />
        </label>
        <Button onClick={apply}>Filter</Button>
      </div>
      {error && <Banner tone="err">{error}</Banner>}
      {loading && entries.length === 0 && <Spinner />}
      {!error && (entries.length > 0 || !loading) && (
        <DataTable
          rows={entries}
          rowKey={(r) => String(r.auditId)}
          emptyState={<div className="empty">No audit rows match.</div>}
          columns={[
            { header: '#', cell: (r) => r.auditId },
            { header: 'When', cell: (r) => fmt(r.createdAt) },
            { header: 'Who', cell: (r) => r.actorUpn },
            { header: 'Route', cell: (r) => <code>{r.route}</code> },
            { header: 'Outcome', cell: (r) => <Pill tone={r.outcome === 'read' || r.outcome === 'ok' || r.outcome === 'updated' ? 'ok' : r.outcome === 'unchanged' ? 'muted' : 'warn'}>{r.outcome}</Pill> },
            {
              header: 'Detail',
              cell: (r) => (
                <code className="value-clip">
                  {r.detail ?? ''}
                  {r.detailTruncated ? '…' : ''}
                </code>
              ),
            },
          ]}
        />
      )}
      {more && last && (
        <div className="card-actions-row">
          <Button variant="ghost" disabled={loading} onClick={() => void load(last.auditId)}>
            {loading ? 'Loading…' : 'Older'}
          </Button>
        </div>
      )}
    </div>
  );
}

const STATUS_OPTIONS: ReadonlyArray<{ value: DeliveryStatusFilter; label: string }> = [
  { value: 'failed', label: 'Failed card deliveries' },
  { value: 'email', label: 'Fell back to email' },
  { value: 'legacy', label: 'Legacy (no delivery status)' },
  { value: 'all', label: 'All recipients' },
];

export function HubDeliveriesPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const status = (STATUS_OPTIONS.find((o) => o.value === params.get('status'))?.value ?? 'failed') as DeliveryStatusFilter;
  const [rows, setRows] = useState<HubDelivery[] | null>(null);
  const [counts, setCounts] = useState<HubDeliveryCount[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    hubApi
      .deliveries(status)
      .then((r) => {
        if (cancelled) return;
        setRows(r.deliveries);
        setCounts(r.counts ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e));
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div>
      <h2>Stuck card deliveries</h2>
      <p className="page-intro">
        Ask recipients whose Teams card did not arrive (HubAskRecipient.LastDeliveryStatus). Open asks are listed first. To
        resend, use the ask&rsquo;s resend-cards action (Operations).
      </p>
      <div className="toolbar">
        <SelectField
          label="Show"
          value={status}
          onChange={(v) => setParams(v === 'failed' ? {} : { status: v })}
          options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
      </div>
      {counts.length > 0 && (
        <p className="form-field__hint">
          {counts.map((c) => `${c.deliveryStatus ?? '(none)'}: ${c.total} (${c.openAsks} on open asks)`).join(' · ')}
        </p>
      )}
      {error && <Banner tone="err">{error}</Banner>}
      {!rows && !error && <Spinner />}
      {rows && (
        <DataTable
          rows={rows}
          rowKey={(r) => String(r.recipientId)}
          emptyState={<div className="empty">Nothing stuck.</div>}
          columns={[
            { header: 'Ask', cell: (r) => `#${r.askId}` },
            { header: 'Subject', cell: (r) => r.subject ?? '—' },
            { header: 'Ask status', cell: (r) => r.status ?? '—' },
            { header: 'Recipient', cell: (r) => `${r.upn ?? '—'}${r.role ? ` (${r.role})` : ''}` },
            { header: 'Delivery', cell: (r) => <Pill tone={r.lastDeliveryStatus === 'failed' ? 'err' : 'muted'}>{r.lastDeliveryStatus ?? 'none'}</Pill> },
            { header: 'Detail', cell: (r) => <span className="value-clip">{r.lastDeliveryDetail ?? ''}</span> },
            { header: 'Last attempt', cell: (r) => fmt(r.lastDeliveryAt) },
            { header: 'Requested', cell: (r) => fmt(r.requestedAt) },
          ]}
        />
      )}
    </div>
  );
}
